import { useState } from "react";
import { PageHeader, Section } from "@/components/site/PageChrome";
import { waLink } from "@/components/site/FloatingWhatsApp";
import { Check, MessageCircle, Star, BadgeCheck, BarChart3, Images, LayoutDashboard, Sparkles } from "lucide-react";

const plans = [
  {
    name: "Shop Lite",
  monthly: "₹249",
  yearly: "₹2,499",
  trial: "1 Month FREE Trial",
  cta: "Start Free Trial",
  tagline: "Start growing your business with a free trial.",
  highlight: false,
    features: [
  "Best for starting locally",
  "Business profile listing",
  "Verified shop profile",
  "Up to 5 gallery images",
  "Standard cover photo",
  "Business description & contact details",
  "Direct customer calls",
  "Get Customers through Whatsapp",
  "Customer enquiries via Time2Cash Chat",
  "List up to 5 premium services",
  "Appear in nearby search results",
  "Display business location on map",
  "Ratings & customer reviews",
],
  },
  {
    name: "Shop Pro+",
  monthly: "₹549",
  yearly: "₹4,999",
  cta: "Choose Shop Pro+",
  tagline: "Everything you need to grow your business faster.",
  highlight: true,
    features: [
  "Everything included in Shop Lite",
  "Best for business growth & priority",
  "Time2Cash Trusted & Verified Shop Badge",
  "Customizable premium cover banner",
  "Up to 15 gallery images",
  "Business description & contact details",
  "Direct customer calls",
  "Get Customers through Whatsapp",
  "Customer enquiries via Time2Cash Chat",
  "List up to 15 premium services",
  "Priority placement in search results",
  "Featured in recommended businesses",
  "Priority for bulk projects from Time2Cash",
  "Promotional campaigns & featured social media posts",
  "Priority customer support",
],
  },
];

export default function ShopRegisterPage() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ shop: "", owner: "", phone: "", city: "", plan: "Shop Lite" });

  const onSubmit = (e) => {
    e.preventDefault();
    const msg = `Hi Time2Cash! I'd like to register my shop.%0A%0AShop: ${encodeURIComponent(form.shop)}%0AOwner: ${encodeURIComponent(form.owner)}%0APhone: ${encodeURIComponent(form.phone)}%0ACity: ${encodeURIComponent(form.city)}%0APlan: ${encodeURIComponent(form.plan)}`;
    window.open(`https://wa.me/919988771606?text=${msg}`, "_blank");
    setSubmitted(true);
  };

  return (
    <>
      <PageHeader
        eyebrow="Shop Registration"
        title="Grow your local business."
        subtitle="Register your shop on Time2Cash, get discovered by nearby customers, and connect directly — no commissions, ever."
      />

      <Section>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {plans.map((p) => (
            <div
              key={p.name}
              className={`rounded-3xl border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
  p.highlight
    ? "border-amber-300 bg-gradient-to-br from-[#FFF9EC] via-white to-[#FFF2CC] shadow-xl ring-2 ring-amber-300/40"
    : "border-sky-200 bg-gradient-to-br from-[#F5FAFF] via-white to-[#EAF6FF] shadow-lg"
}`}
            >
              {p.highlight ? (
  <div className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-4 py-1 text-xs font-semibold shadow">
    <Sparkles className="w-3.5 h-3.5" />
    Most Popular
  </div>
) : (
  <div className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 text-white px-4 py-1 text-xs font-semibold shadow">
    🎉 1 Month FREE Trial
  </div>
)}
              <h3 className="mt-3 text-2xl font-bold text-[#1A1918]">{p.name}</h3>
              <p className="text-sm text-[#78716C]">{p.tagline}</p>
              <div className="mt-5">

  {!p.highlight && (
    <div className="mb-3 rounded-xl bg-sky-100 border border-sky-200 px-4 py-3">
      <div className="text-sm font-bold text-sky-700">
        🎁 {p.trial}
      </div>
      <div className="text-xs text-sky-600 mt-1">
        Try Shop Lite absolutely free before choosing a paid plan.
      </div>
    </div>
  )}

  <div className="flex items-end gap-2">
    <span className="text-4xl font-extrabold text-[#1A1918]">
      {p.monthly}
    </span>
    <span className="text-[#78716C] mb-1">
      /month
    </span>
  </div>

  <div className={`mt-4 rounded-xl px-4 py-3 ${
    p.highlight
      ? "bg-amber-50 border border-amber-200"
      : "bg-sky-50 border border-sky-200"
  }`}>

    <div className={`text-xs uppercase font-semibold ${
      p.highlight
        ? "text-amber-600"
        : "text-sky-700"
    }`}>
      Yearly Plan
    </div>

    <div className="mt-1 text-2xl font-bold text-[#1A1918]">
      {p.yearly}
      <span className="text-base text-[#78716C]">
        {" "} /year
      </span>
    </div>

  </div>

</div>
              <ul className="mt-6 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#4A4846]">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <a
  href="#register"
  className={`mt-8 block rounded-full py-3.5 text-center font-bold transition-all duration-300 hover:scale-105 ${
    p.highlight
      ? "bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-lg hover:shadow-xl"
      : "bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg hover:shadow-xl"
  }`}
>
  {p.highlight ? "Choose Shop Pro+" : "Start Free Trial"}
</a>
            </div>
          ))}
        </div>
      </Section>

      <Section className="bg-[#FFF9F5]">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A1918]">Why shop owners love us.</h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: BadgeCheck, t: "Verified Profile" },
            { icon: BarChart3, t: "Business Analytics" },
            { icon: MessageCircle, t: "Customer Enquiries" },
            { icon: Images, t: "Gallery Showcase" },
            { icon: LayoutDashboard, t: "Business Dashboard" },
            { icon: Star, t: "Ratings & Reviews" },
          ].map(({ icon: Icon, t }) => (
            <div key={t} className="flex items-center gap-3 rounded-2xl bg-white border border-[#EBE8E3] p-4">
              <Icon className="w-5 h-5 text-[#FF5A00]" />
              <span className="font-semibold text-[#1A1918]">{t}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section id="register">
        <div className="max-w-xl mx-auto rounded-3xl border border-[#EBE8E3] bg-white p-8 shadow-sm">
          <h2 className="text-2xl font-bold text-[#1A1918]">Register your shop</h2>
          <p className="text-sm text-[#78716C] mt-1">Submit and we&apos;ll continue on WhatsApp to set you up.</p>
          {!submitted ? (
            <form onSubmit={onSubmit} data-testid="shop-form" className="mt-6 space-y-4">
              <input required data-testid="shop-name" placeholder="Shop name" value={form.shop} onChange={(e) => setForm({ ...form, shop: e.target.value })} className="w-full rounded-xl border border-[#EBE8E3] bg-[#FDFBF7] px-4 py-3 text-sm outline-none focus:border-[#FF5A00]" />
              <input required data-testid="shop-owner" placeholder="Owner name" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="w-full rounded-xl border border-[#EBE8E3] bg-[#FDFBF7] px-4 py-3 text-sm outline-none focus:border-[#FF5A00]" />
              <div className="grid grid-cols-2 gap-3">
                <input required type="tel" data-testid="shop-phone" placeholder="Phone (+91)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-[#EBE8E3] bg-[#FDFBF7] px-4 py-3 text-sm outline-none focus:border-[#FF5A00]" />
                <input required data-testid="shop-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full rounded-xl border border-[#EBE8E3] bg-[#FDFBF7] px-4 py-3 text-sm outline-none focus:border-[#FF5A00]" />
              </div>
              <select data-testid="shop-plan" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="w-full rounded-xl border border-[#EBE8E3] bg-[#FDFBF7] px-4 py-3 text-sm outline-none focus:border-[#FF5A00]">
                <option>Shop Lite</option>
                <option>Shop Pro+</option>
              </select>
              <button type="submit" data-testid="shop-submit" className="w-full rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white py-3.5 font-semibold flex items-center justify-center gap-2 transition-colors">
                <MessageCircle className="w-4 h-4" /> Register via WhatsApp
              </button>
            </form>
          ) : (
            <div data-testid="shop-success" className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="mt-4 text-xl font-bold text-[#1A1918]">Almost there!</h3>
              <p className="mt-2 text-[#4A4846]">We&apos;ve opened WhatsApp with your details pre-filled. Send the message to complete registration.</p>
            </div>
          )}
        </div>
      </Section>
    </>
  );
}
